import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Plus, Trash2, Save, ChevronLeft, ChevronRight, SlidersHorizontal, Filter, Pencil, Target, ImageOff,
} from 'lucide-react';
import AppLayout from '../components/AppLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Card } from '../components/ui/card';
import { Dialog, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { api } from '../api/feedy';
import { saveCollection } from '../utils/saveCollection';
import { ConditionsEditor, ActionsEditor } from '../components/feeds/RuleBuilder';

// ---- "which items does this rule affect?" preview -------------------------
function AffectedItemsDialog({ rule, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!rule?.id) return;
    setLoading(true); setError(''); setData(null);
    api.masterRules.affected(rule.id)
      .then(setData)
      .catch(() => setError('Could not load affected items.'))
      .finally(() => setLoading(false));
  }, [rule?.id]);

  return (
    <Dialog open={!!rule} onClose={onClose} className="max-w-4xl">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Target className="h-5 w-5 text-primary" />
          Affected items — {rule?.name || 'rule'}
        </DialogTitle>
        <DialogDescription>
          {loading ? 'Scanning the catalog…'
            : error ? error
            : data ? <>Matched <b className="text-foreground tnum">{data.matched.toLocaleString()}</b> of {data.total.toLocaleString()} catalog items{data.sample.length < data.matched ? ` — showing the first ${data.sample.length}` : ''}.</>
            : 'Save the rule first to preview its matches.'}
        </DialogDescription>
      </DialogHeader>

      <div className="max-h-[70vh] overflow-y-auto pr-1">
        {loading && (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="animate-pulse rounded-lg border border-border p-2">
                <div className="aspect-square rounded-md bg-muted" />
                <div className="mt-2 h-3 w-3/4 rounded bg-muted" />
              </div>
            ))}
          </div>
        )}

        {!loading && data && data.sample.length === 0 && (
          <p className="py-10 text-center text-sm text-muted-foreground">No catalog items match this rule.</p>
        )}

        {!loading && data && data.sample.length > 0 && (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
            {data.sample.map((it) => (
              <div key={it.external_id} className="flex flex-col rounded-lg border border-border bg-card p-2">
                <div className="grid aspect-square place-items-center overflow-hidden rounded-md border border-border bg-white">
                  {it.image
                    ? <img src={it.image} alt="" loading="lazy" className="h-full w-full object-contain" />
                    : <ImageOff className="h-6 w-6 text-muted-foreground" />}
                </div>
                <div className="mt-1.5 line-clamp-2 text-[12px] font-medium leading-tight" title={it.title}>{it.title}</div>
                {it.value && <div className="mt-1 truncate rounded bg-primary/10 px-1.5 py-0.5 font-mono text-[10px] text-foreground/80" title={it.value}>{it.value}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </Dialog>
  );
}

// Colour system: filters (exclude/keep) = rose, edits = sky/violet/emerald.
const TYPE_STYLE = {
  exclude: { dot: 'bg-rose-500', chip: 'bg-rose-500/10 text-rose-600 border-rose-500/30', label: 'Exclude' },
  keep: { dot: 'bg-amber-500', chip: 'bg-amber-500/10 text-amber-600 border-amber-500/30', label: 'Keep' },
  set: { dot: 'bg-sky-500', chip: 'bg-sky-500/10 text-sky-600 border-sky-500/30', label: 'Set' },
  replace: { dot: 'bg-violet-500', chip: 'bg-violet-500/10 text-violet-600 border-violet-500/30', label: 'Replace' },
  combine: { dot: 'bg-emerald-500', chip: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30', label: 'Combine' },
};
const actionsOf = (rule) => (Array.isArray(rule.action) ? rule.action : [rule.action || {}]);
const primaryType = (rule) => (actionsOf(rule)[0] || {}).type || 'set';
const isFilter = (rule) => ['exclude', 'keep'].includes(primaryType(rule));
const styleFor = (t) => TYPE_STYLE[t] || TYPE_STYLE.set;

const emptyRule = () => ({
  name: '', enabled: true,
  condition: { match: 'all', conditions: [{ field: '', operator: 'equals', value: '' }] },
  action: { type: 'set', field: '', value: '' },
});

// ---- detail panel: full editor for every rule (filters, multi-action, nesting)
function RuleDetail({ rule, internalFields, onPatch, onDelete, onShowAffected }) {
  const isFilterRule = isFilter(rule);
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <Input value={rule.name} onChange={(e) => onPatch({ name: e.target.value })} placeholder="Rule name" className="h-9 max-w-md text-base font-semibold" />
        <div className="ml-auto flex items-center gap-2">
          <Button
            variant="secondary" size="sm" className="gap-1.5"
            onClick={onShowAffected} disabled={!rule.id}
            title={rule.id ? 'Preview which catalog items this rule matches' : 'Save the rule first to preview its matches'}
          >
            <Target className="h-4 w-4" /> Affected items
          </Button>
          <span className="text-xs text-muted-foreground">Enabled</span>
          <Switch checked={rule.enabled} onCheckedChange={(v) => onPatch({ enabled: v })} />
          <Button variant="ghost" size="icon" onClick={onDelete} aria-label="Delete rule"><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>
      {isFilterRule && (
        <p className="-mt-2 text-[11px] text-muted-foreground">This is a {primaryType(rule)} filter — “Affected items” shows the items its condition matches.</p>
      )}

      {/* IF */}
      <div className="rounded-lg border border-l-4 border-l-amber-400 bg-amber-500/[0.04] p-4">
        <div className="mb-2 flex items-center gap-2"><Filter className="h-4 w-4 text-amber-600" /><span className="text-sm font-semibold">IF</span></div>
        <ConditionsEditor
          condition={rule.condition?.conditions ? rule.condition : { match: 'all', conditions: [] }}
          fields={internalFields}
          onChange={(c) => onPatch({ condition: c })}
        />
      </div>

      {/* THEN */}
      <div className="rounded-lg border border-l-4 border-l-sky-400 bg-sky-500/[0.04] p-4">
        <div className="mb-2 flex items-center gap-2"><Pencil className="h-4 w-4 text-sky-600" /><span className="text-sm font-semibold">THEN</span></div>
        <ActionsEditor action={rule.action} fields={internalFields} onChange={(a) => onPatch({ action: a })} />
      </div>
    </div>
  );
}

// ---- per-group two-pane editor --------------------------------------------
function GroupEditor({ group, internalFields, onBack, onChanged, focusRuleId }) {
  const [rules, setRules] = useState([]);
  const [sel, setSel] = useState(0);
  const [saving, setSaving] = useState(false);
  const [origIds, setOrigIds] = useState([]);
  const [affectedRule, setAffectedRule] = useState(null);

  useEffect(() => {
    const init = [...(group.rules || [])].sort((a, b) => a.order - b.order);
    setRules(init); setOrigIds(init.filter((r) => r.id).map((r) => r.id));
    setSel(Math.max(0, init.findIndex((r) => r.id === focusRuleId)));  // deep link from a feed's Excluded tab
  }, [group.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const patch = (i, p) => setRules((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...p } : r)));
  const addRule = () => setRules((rs) => { setSel(rs.length); return [...rs, emptyRule()]; });
  const delRule = (i) => setRules((rs) => rs.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      const res = await saveCollection(rules, origIds, {
        payload: (r, idx) => ({ group: group.id, name: r.name, order: idx, enabled: r.enabled, condition: r.condition, action: r.action }),
        create: api.masterRules.create, update: api.masterRules.update, remove: api.masterRules.remove,
      });
      setRules(res.rows);
      setOrigIds(res.ids);
      if (!res.error) await onChanged();  // failures were already shown as a toast
    } finally { setSaving(false); }
  };

  const current = rules[sel];
  return (
    <div className="flex h-[calc(100vh-8.5rem)] gap-4">
      {/* left: rule list */}
      <div className="flex w-72 shrink-0 flex-col rounded-lg border border-border">
        <div className="flex items-center justify-between border-b border-border px-3 py-2.5">
          <button onClick={onBack} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ChevronLeft className="h-4 w-4" /> Groups</button>
          <Button size="sm" variant="ghost" className="gap-1" onClick={addRule}><Plus className="h-4 w-4" /> Rule</Button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-1.5">
          {rules.map((r, i) => {
            const t = primaryType(r); const s = styleFor(t);
            return (
              <div key={r.id ?? `n${i}`}
                className={`group/rule mb-0.5 flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm transition-colors ${i === sel ? 'bg-muted font-medium' : 'hover:bg-muted/60'} ${!r.enabled ? 'opacity-50' : ''}`}>
                <span className={`h-2 w-2 shrink-0 rounded-full ${s.dot}`} title={s.label} />
                <button onClick={() => setSel(i)} className="min-w-0 flex-1 truncate text-left">{r.name || `Rule ${i + 1}`}</button>
                {isFilter(r) && <Filter className="h-3 w-3 shrink-0 text-rose-500" />}
                {r.id && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setAffectedRule(r); }}
                    className="shrink-0 rounded p-0.5 text-muted-foreground opacity-0 transition-opacity hover:text-primary group-hover/rule:opacity-100"
                    title="Affected items" aria-label="Affected items"
                  >
                    <Target className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            );
          })}
          {rules.length === 0 && <p className="px-3 py-6 text-center text-xs text-muted-foreground">No rules yet.</p>}
        </div>
        <div className="border-t border-border p-2">
          <Button size="sm" className="w-full gap-1.5" onClick={save} disabled={saving}><Save className="h-4 w-4" /> {saving ? 'Saving…' : 'Save all'}</Button>
        </div>
      </div>

      {/* right: detail */}
      <div className="min-w-0 flex-1 overflow-y-auto rounded-lg border border-border p-5">
        {current ? (
          <RuleDetail rule={current} internalFields={internalFields}
            onPatch={(p) => patch(sel, p)} onDelete={() => delRule(sel)}
            onShowAffected={() => setAffectedRule(current)} />
        ) : (
          <div className="grid h-full place-items-center text-sm text-muted-foreground">Select or add a rule.</div>
        )}
      </div>

      {affectedRule && <AffectedItemsDialog rule={affectedRule} onClose={() => setAffectedRule(null)} />}
    </div>
  );
}

const COLORS = ['#2563eb', '#f59e0b', '#16a34a', '#dc2626', '#7c3aed', '#0891b2', '#64748b'];

export default function MasterRules() {
  const [groups, setGroups] = useState([]);
  const [internalFields, setInternalFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [params] = useSearchParams();
  const focusRuleId = Number(params.get('rule')) || null;
  useEffect(() => { const g = Number(params.get('group')); if (g) setOpenId(g); }, [params]);

  const load = async () => {
    const [g] = await Promise.all([
      api.masterRuleGroups.list().catch(() => []),
      api.items.fields().then(setInternalFields).catch(() => {}),
    ]);
    setGroups(g || []); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const newGroup = async () => {
    await api.masterRuleGroups.create({ name: 'New master rule group', order: groups.length, color: COLORS[0] });
    await load();
  };
  const removeGroup = async (id, name) => {
    if (window.confirm(`Delete group "${name}" and its rules?`)) { await api.masterRuleGroups.remove(id); await load(); }
  };

  const openGroup = groups.find((g) => g.id === openId);

  return (
    <AppLayout
      title="Master rules"
      description="Global rules that adapt the unified catalog before any feed is built — filters and edits, organised into groups."
      actions={!openId && <Button className="gap-1.5" onClick={newGroup}><Plus className="h-4 w-4" /> New master rule group</Button>}
    >
      {loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : openGroup ? (
        <GroupEditor group={openGroup} internalFields={internalFields} onBack={() => setOpenId(null)} onChanged={load} focusRuleId={focusRuleId} />
      ) : groups.length === 0 ? (
        <Card className="flex flex-col items-center gap-3 p-12 text-center">
          <SlidersHorizontal className="h-8 w-8 text-muted-foreground" />
          <p className="font-medium">No master rule groups yet</p>
          <Button className="gap-1.5" onClick={newGroup}><Plus className="h-4 w-4" /> New master rule group</Button>
        </Card>
      ) : (
        <Card className="divide-y divide-border">
          {groups.map((g) => {
            const n = g.rule_count ?? g.rules?.length ?? 0;
            return (
              <div key={g.id} className="flex items-center gap-2.5 px-3 py-2">
                <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: g.color || COLORS[0], opacity: g.enabled ? 1 : 0.4 }} />
                <button onClick={() => setOpenId(g.id)} className="text-sm font-medium hover:underline">{g.name}</button>
                <span className="font-mono text-[11px] text-muted-foreground tnum">{n} rule{n === 1 ? '' : 's'}</span>
                {!g.enabled && <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase text-muted-foreground">off</span>}
                <div className="ml-auto flex items-center gap-0.5">
                  <Button size="sm" variant="ghost" className="h-8 gap-1 text-muted-foreground" onClick={() => setOpenId(g.id)}>Open <ChevronRight className="h-3.5 w-3.5" /></Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeGroup(g.id, g.name)} aria-label="Delete group"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            );
          })}
        </Card>
      )}
    </AppLayout>
  );
}
